import { PERMISSIONS } from '../store/permissionStore';
import type { User } from '../types';
import { hasFullAccessRole } from '../utils/fullAccessRole';

export type AppRole = User['role'];

/** 需角色/权限守卫的业务模块；个人设置 /settings 对所有已登录用户开放，不在此列 */
export type SystemRouteKey = 'hr';

export const APP_ROLE_LABELS: Record<AppRole, string> = {
  super_admin: '超级管理员',
  admin: '系统管理员',
  manager: '业务经理',
  sales: '销售',
  consultant: '顾问',
};

export const HR_PERMISSION_MATRIX = {
  canViewAllEmployees: [PERMISSIONS.HR.EMPLOYEE.LIST],
  canEditEmployees: [PERMISSIONS.HR.EMPLOYEE.EDIT],
  canApproveLeave: [PERMISSIONS.HR.LEAVE.APPROVE],
  canApproveResign: [PERMISSIONS.HR.RESIGN.APPROVE, PERMISSIONS.HR.RESIGN.LIST],
  canViewSalary: [PERMISSIONS.HR.SALARY.LIST],
  canExportData: [PERMISSIONS.HR.EMPLOYEE.LIST],
  canManageTransfer: [PERMISSIONS.HR.EMPLOYEE.EDIT],
} as const;

export type HrUiPerm = keyof typeof HR_PERMISSION_MATRIX;

export function hasRouteAccess(
  _role: AppRole | undefined,
  _permissions: string[],
  routeKey: SystemRouteKey,
): boolean {
  // 人事管理：与其它业务模块一致，所有已登录用户均可进入（SystemRouteGuard 已校验登录态）。
  // 进入后由 HrWorkspace 按权限切换「完整管理页 / 本人自助页」，数据范围由后端隔离。
  if (routeKey === 'hr') {
    return true;
  }
  return false;
}

/** 是否为 HR 管理者：决定 HrWorkspace 渲染完整管理页(true) 还是本人自助页(false) */
export function hasHrAdminMenuAccess(role: AppRole | undefined, permissions: string[]): boolean {
  if (hasFullAccessRole(role)) {
    return true;
  }
  // 后端 role=hr_manager 在前端常映射为 manager
  if (role === 'manager') {
    return true;
  }
  if (permissions.includes(PERMISSIONS.HR.LEAVE.APPROVE)) {
    return true;
  }
  if (permissions.includes(PERMISSIONS.HR.RESIGN.LIST)) {
    return true;
  }
  if (permissions.includes(PERMISSIONS.HR.SALARY.LIST) && permissions.includes(PERMISSIONS.HR.EMPLOYEE.EDIT)) {
    return true;
  }
  if (permissions.includes(PERMISSIONS.HR.CONTRACT.LIST) && permissions.includes(PERMISSIONS.HR.EMPLOYEE.LIST)) {
    return true;
  }
  return false;
}

export function getHrPermissionCodes(key: HrUiPerm): readonly string[] {
  return HR_PERMISSION_MATRIX[key];
}
